import React from "react";
import { SaveBar, useAppBridge } from "@shopify/app-bridge-react";
import { useBlocker } from "react-router-dom";

/**
 * A reusable save bar component using Shopify App Bridge SaveBar.
 * Wraps the Shopify App Bridge SaveBar with convenient props.
 * Optionally blocks navigation when the save bar is open.
 *
 * @param {boolean} blockNavigation - Whether to block navigation when save bar is open.
 *                                    Defaults to false. Set to true when there are actual unsaved changes.
 * @param {React.MutableRefObject<boolean>} allowNavigationRef - Optional ref that can be set to true
 *                                    to allow navigation (bypasses blocker). Useful when navigation
 *                                    is triggered from outside the save bar (e.g., confirmation dialogs).
 */
export default function AppBridgeSaveBar({
  id = "app-save-bar",
  open = false,
  saveAction,
  discardAction,
  blockNavigation = false,
  allowNavigationRef: externalAllowNavigationRef,
}) {
  const shopify = useAppBridge();

  // Use internal ref if no external ref provided
  const internalAllowNavigationRef = React.useRef(false);
  const allowNavigationRef = externalAllowNavigationRef || internalAllowNavigationRef;

  // shopify.saveBar.hide returns a Promise that rejects if the SaveBar is
  // already hidden/destroyed. An uncaught rejection inside an App Bridge
  // event subscriber wedges App Bridge's dispatcher (NavMenu stops reacting),
  // so swallow it explicitly — sync try/catch does not cover promise reject.
  const safeHide = React.useCallback(() => {
    try {
      const p = shopify.saveBar.hide(id);
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {
      // ignore
    }
  }, [shopify, id]);

  // Hide save bar when component unmounts
  React.useEffect(() => {
    return () => {
      safeHide();
    };
  }, [safeHide]);

  // Use blocker to intercept navigation - always intercept when save bar is open,
  // but only actually block if blockNavigation is true
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      open && !allowNavigationRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  // When navigation is intercepted, hide save bar and proceed if not blocking.
  // Upgrade flows navigating to /subscription always proceed so unsaved drafts
  // are preserved via the existing unmount-time persistence; the explicit
  // safeHide here ensures App Bridge removes the SaveBar before the route change.
  React.useEffect(() => {
    if (blocker.state === "blocked") {
      const goingToSubscription = blocker.location?.pathname?.startsWith("/subscription");
      if (blockNavigation && !goingToSubscription) {
        blocker.reset();
      } else {
        safeHide();
        blocker.proceed();
      }
    }
  }, [blocker, blockNavigation, safeHide]);

  const handleSave = () => {
    if (saveAction?.onAction) {
      saveAction.onAction();
    }
  };

  const handleDiscard = () => {
    // Allow navigation before calling the discard action (which may navigate)
    allowNavigationRef.current = true;
    if (discardAction?.onAction) {
      discardAction.onAction();
    }
  };

  return (
    <SaveBar id={id} open={open}>
      <button
        variant="primary"
        onClick={handleSave}
        disabled={!!saveAction?.disabled}
        loading={!!saveAction?.loading}
      />
      <button
        onClick={handleDiscard}
        disabled={!!discardAction?.disabled}
        loading={!!discardAction?.loading}
      />
    </SaveBar>
  );
}
